import { auth as firebaseAuth } from 'boot/firebase';
import { documentId, serverTimestamp } from 'firebase/firestore';
import {
  chain, chunk, compact, concat, find, flatten, get, isEmpty, remove, size,
} from 'lodash';
import { useClients } from 'src/composables/clients';
import ClientsDB from 'src/fb/clients-db';
import GenericDB from 'src/fb/generic-db';
import UsersClientsDB from 'src/fb/usersClients-db';
import { auth as useAuthStore } from 'src/store/auth';
import { getClientInitials, userRoles } from 'src/utils/AppUtils';
import { bindDataBaseStore, unbindDataBaseStore } from 'src/utils/FirestoreUtils';
import { logEvent } from 'src/utils/LogEvent';

/**
 * Client ids read per query. Not Firestore's `in` limit (30) but the rules budget: a query may make 10
 * `get()`/`exists()` calls, and `canReadClient` spends one on the user plus one `usersClients` lookup per id.
 */
const MAX_CLIENT_IDS_PER_READ = 9;

/**
 * Chunks read at once. A clinic with thousands of associated clients produces hundreds of chunks, and
 * handing all of them to `Promise.all` queues them against the browser's connection limit instead of
 * running them in parallel, so the reads finish slower than they would in waves.
 */
const MAX_CONCURRENT_READS = 6;

/**
 * @template T, R
 * @param {T[]} items
 * @param {(item: T) => Promise<R>} read
 * @returns {Promise<R[]>}
 */
async function readInWaves(items, read) {
  const results = [];

  for (let start = 0; start < items.length; start += MAX_CONCURRENT_READS) {
    // eslint-disable-next-line no-await-in-loop -- the wait is the point: it bounds concurrency
    results.push(...await Promise.all(items.slice(start, start + MAX_CONCURRENT_READS).map(read)));
  }

  return results;
} // readInWaves()

export default {

  setIsLoadingClients(isLoadingClients) {
    this.isLoadingClients = isLoadingClients;
  }, // setIsLoadingClients()

  pushLoadedClient(client) {
    this.loadedClients.push(client);
  }, // pushLoadedClient()

  setLoadedClients(loadedClients) {
    this.loadedClients = loadedClients;
  }, // setLoadedClients()

  /**
   * @param {Object & {id: string}} client
   */
  replaceLoadedClient(client) {
    if (!client?.id) {
      return;
    }

    const index = this.loadedClients.findIndex((loadedClient) => loadedClient.id === client.id);
    if (index === -1) {
      this.loadedClients.push(client);
    } else {
      this.loadedClients.splice(index, 1, client);
    }
  }, // replaceLoadedClient()

  removeLoadedClient(clientId) {
    remove(this.loadedClients, { id: clientId });
  }, // removeLoadedClient

  /**
   * @param {string[]} notificationsIds
   */
  setClientsTriggerNotificationsToIgnore(notificationsIds) {
    this.clientsTriggerNotificationsToIgnore = notificationsIds;
  }, // setClientsTriggerNotificationsToIgnore()

  /**
   * Bind client relationships to managed clinic
   */
  async initializeClients() {
    const auth = useAuthStore();

    try {
      this.setIsLoadingClients(true);
      const userRole = auth.userDetails?.role || '';

      if ([userRoles.MANAGER, userRoles.ADMINISTRATOR].includes(userRole)) {
        const clinicId = auth.userDetails?.clinic || '';

        // If user is not associated with any clinic, we can't load clients
        if (!clinicId) {
          return;
        }

        const clientsDb = new ClientsDB();
        const ref = clientsDb.getQuery([['clinic', '==', clinicId]]);

        await bindDataBaseStore({ store: this, property: 'managedClients' }, ref, []);
      } else if (!isEmpty(auth.currentClient) && isEmpty(this.loadedClients)) {
        this.pushLoadedClient(auth.currentClient);
      }
    } catch (err) {
      console.error('Error loading clients', err);
      throw err;
    } finally {
      this.setIsLoadingClients(false);
    }
  }, // bindManagedClients

  /**
   * Reload associated clients
   *
   */
  async syncClients() {
    const auth = useAuthStore();

    if (auth.isManagerOrAdministrator) {
      return;
    }

    const associatedClientIds = auth.associatedClients.map((associatedClient) => ({ id: associatedClient.client }));
    // Remove clients that are no longer available
    this.setLoadedClients(chain(this.loadedClients)
      .intersectionBy(associatedClientIds, 'id')
      .value());

    // Load clients that were added
    const clientsDb = new ClientsDB();
    const clientIdsToLoad = chain(associatedClientIds)
      .differenceBy(this.loadedClients, 'id')
      .map((client) => client.id)
      .value();

    if (clientIdsToLoad.length === 0) {
      return;
    }

    this.setIsLoadingClients(true);

    let newLoadedClients;

    try {
      const chunkedIds = chunk(clientIdsToLoad, MAX_CLIENT_IDS_PER_READ);
      newLoadedClients = await readInWaves(chunkedIds, async (idsChunk) => {
        try {
          return await clientsDb.readAll([[documentId(), 'in', idsChunk]]);
        } catch (err) {
          const logData = { documentsIds: idsChunk };
          console.error(err.message, logData);
          logEvent(err, {
            context: {
              context_data: { logData },
            },
          });
          throw err;
        }
      });
      newLoadedClients = flatten(newLoadedClients);

      this.setLoadedClients(concat(this.loadedClients, compact(newLoadedClients)));
    } catch (err) {
      console.error('Error loading associated clients', err);
      logEvent(new Error(`Error loading associated clients: ${err.message}`), {
        context: {
          context_data: { clientIdsToLoad },
        },
      });
    } finally {
      this.setIsLoadingClients(false);
    }
  }, // syncClients()

  /**
   * Update specific client data
   *
   * @param {string} clientId
   * @returns {Promise<void>}
   */
  async syncClient(clientId) {
    const auth = useAuthStore();

    if (auth.isManagerOrAdministrator || !clientId) {
      return;
    }

    this.setIsLoadingClients(true);
    try {
      const clientsDb = new ClientsDB();
      const client = await clientsDb.read(clientId);
      if (!client || !client.id) {
        return;
      }

      this.replaceLoadedClient(client);

      if (auth.currentClient.id === clientId) {
        await auth.updateCurrentClient(client);
      }
    } finally {
      this.setIsLoadingClients(false);
    }
  }, // syncClient()

  /**
   * Create a client
   *
   */
  async createClient(client) {
    const clientsDb = new ClientsDB();
    const { getRandomColor } = useClients();

    // Add avatar initials and background color if not already present
    if (!client.avatarInitials || !client.avatarBgColor) {
      client.avatarInitials = getClientInitials(client.clientName);
      client.avatarBgColor = client.avatarBgColor ?? getRandomColor();
    }

    const createdClient = await clientsDb.create(client);
    this.replaceLoadedClient(createdClient);

    const auth = useAuthStore();

    // Add a relationship between created client and user if user is supervisor
    if (get(auth, ['userDetails', 'role'], '') === userRoles.SUPERVISOR) {
      const usersClientsObj = {
        user: get(auth, ['userDetails', 'userId']),
        client: createdClient.id,
        userName: get(auth, ['userDetails', 'name']),
        role: get(auth, ['userDetails', 'role']),
        userPrivilege: get(auth, ['userDetails', 'userPrivilege'], null),
        invitedByUser: get(auth, ['userDetails', 'userId']),
      };
      const userClientId = `${usersClientsObj.user}_${usersClientsObj.client}`;

      const usersClientsDb = new UsersClientsDB();
      await usersClientsDb.create(usersClientsObj, userClientId);
    }

    return createdClient;
  }, // createClient()

  /**
   * Update info about a client
   */
  async updateClient(client) {
    const clientsDb = new ClientsDB();
    await clientsDb.update(client);

    await this.syncClient(client.id);
  }, // updateClient()

  /**
   * Delete a client
   * @param {string} clientId
   * @returns {Promise<boolean>} whether a client record was actually deleted - false when it was
   * already gone, which callers doing their own bookkeeping (e.g. license reconciliation) need to
   * distinguish from a real deletion.
   */
  async deleteClient(clientId) {
    const auth = useAuthStore();

    if (!isEmpty(auth.currentClient)
      && (auth.currentClient.id === clientId)
    ) {
      await auth.changeCurrentClient({});
    }

    const clientsDb = new ClientsDB();

    let client;

    try {
      client = await clientsDb.read(clientId);
    } catch (err) {
      console.warn('Client might have already been deleted.', { clientId });
    }

    let deleted = false;

    // In some cases client might have already been deleted, so we need to check if it exists before trying to update it
    if (client) {
      // We need to update the `updateBy` field of the client before deleting it to make sure we have valid audit trail
      await clientsDb.update({ id: clientId });

      const usersClientsDb = new UsersClientsDB();
      await usersClientsDb.deleteByClient(clientId);

      await clientsDb.delete(clientId);
      deleted = true;
    }

    this.removeLoadedClient(clientId);

    return deleted;
  }, // deleteClient()

  /**
   * Soft-delete a client.
   * No-ops if the client is missing or already tombstoned.
   * @param {string} clientId
   * @returns {Promise<boolean>} whether this call wrote the tombstone
   */
  async softDeleteClient(clientId) {
    const auth = useAuthStore();

    if (auth.currentClient?.id === clientId) {
      await auth.changeCurrentClient({});
    }

    const clientsDb = new ClientsDB();
    const usersClientsDb = new UsersClientsDB();

    // The tombstone and the access-row delete commit together. The transaction cannot lock the
    // usersClients query, so a grant arriving after it is refused by canCreateUserClients instead.
    const deleted = await new GenericDB().executeTransaction(async (transaction) => {
      const client = await transaction.read(clientsDb, { id: clientId });

      if (!client || client.isDeleted) {
        return false;
      }

      const usersClients = await transaction.readAll(usersClientsDb, {
        constraints: [['client', '==', clientId]],
      });

      await transaction.update(clientsDb, {
        data: {
          id: clientId,
          isDeleted: true,
          deleteTimestamp: serverTimestamp(),
          // isTombstonedByCaller() in rules compares this to request.auth.uid.
          deletedBy: firebaseAuth?.currentUser?.uid ?? null,
          // Access rows are hard-deleted so they stop granting access; restoreClient recreates them from this.
          deletedUsersClients: usersClients.map(({ user, invitedByUser }) => ({
            user,
            invitedByUser: invitedByUser ?? null,
          })),
          // Without `type`, restore and cleanup would never find the tombstone.
          ...(!client.type && { type: 'client' }),
        },
      });

      await Promise.all(usersClients.map(({ id }) => transaction.delete(usersClientsDb, id)));
      return true;
    });

    this.removeLoadedClient(clientId);

    return deleted;
  }, // softDeleteClient()

  /**
   * Load a clinic's soft-deleted clients, for the recovery dialog.
   * @param {string} clinicId
   * @returns {Promise<Array>}
   */
  async loadDeletedClients(clinicId) {
    const clientsDb = new ClientsDB();
    return clientsDb.readAll([
      ['clinic', '==', clinicId],
      ['type', '==', 'client'],
      ['isDeleted', '==', true],
    ]);
  }, // loadDeletedClients()

  /**
   * Load a staff member by its user id and cache it if not already loaded
   * @param {string} userId
   * @returns {Object} staff member
   */
  async getStaffMemberByUser(userId) {
    if (!userId) {
      return null;
    }
    const staffMember = find(this.clients, { user: userId });
    if (staffMember) {
      return staffMember;
    }

    const auth = useAuthStore();

    const clinicId = auth.userDetails.clinic;
    const clientsDb = new ClientsDB();
    const staffMembers = await clientsDb.readAll([
      ['clinic', '==', clinicId],
      ['type', '==', 'staff'],
      ['user', '==', userId],
    ], null, null, 1);

    if (size(staffMembers) > 0) {
      this.pushLoadedClient(staffMembers[0]);
      return staffMembers[0];
    }
    return null;
  }, // getStaffMemberByUser()

  async logout() {
    await unbindDataBaseStore({ store: this, property: 'managedClients' }, []);
    this.setLoadedClients([]);
    this.setIsLoadingClients(false);
  }, // logout()

  addClientsNotificationToIgnore(notificationId) {
    this.clientsTriggerNotificationsToIgnore.push(notificationId);
  }, // addClientsNotificationToIgnore()

};
